import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import * as am4charts from '@amcharts/amcharts4/charts';
import moment from 'moment-timezone';
import { useTranslation } from '@lib/useTypedTranslation';

import { DeviceInfoContext } from '../../../../device';
import { DataUsage24Hours } from '../../../../device/compositions/performance/charts/24HourDataUsage';
import { filterDeviceEvents, getDeviceEvents } from '../../../../device/compositions/performance/events/deviceEvents';
import { getActiveEventFilters } from '../../../../device/compositions/performance/events/edit-events';
import { getSelectedTzDateDeviceEventsList } from '../../../../../../services/core/devicePerformance';
import {
  getDeviceAnomalyDataUsageHourly,
  IDeviceAnomalyDataUsageHourlyPoint
} from '../../../../../../services/core/deviceAnomaliesDataUsageHourly';
import { TzDeviceEvent } from '../../../../../../services/core/eventsTypes';
import { RequestInitWithRetry } from '../../../../../../lib/request';
import { useWorldRequest } from '../../../../../../lib/useWorldRequest';
import { useFeatureTogglesContext } from '../../../../../../context/featureToggles';
import { useWorldSettingsContext } from '../../../../../../context/worldSettings';
import { Loading } from '../../../../../../components/loading/loading';
import { italicAxisTitle } from '../../../../../../components/chart/lib';
import { useAnomalyExaminationEventFilters } from '../useAnomalyExaminationEventFilters';
import { AnomalySectionProps } from './index';

import '../../../../device/compositions/performance/reports.css';

const INITIALLY_ENABLED_EVENTS = [] as const;

type SectionData = [IDeviceAnomalyDataUsageHourlyPoint[], TzDeviceEvent[]];

export function DataUsageHourlySection(props: AnomalySectionProps) {
  const { deviceId, selectedDate, onChartMount } = props;
  const { t } = useTranslation(['anomalyDetectionBeta', 'translation']);
  const { platformType, timeZone } = useContext(DeviceInfoContext);
  const featureToggles = useFeatureTogglesContext();
  const { worldSettings } = useWorldSettingsContext();

  const { eventFilters, dispatchEventFilters } = useAnomalyExaminationEventFilters(
    platformType, INITIALLY_ENABLED_EVENTS
  );

  const anomalyTz = worldSettings.smartGroupingTimeZone || 'UTC';

  const selectedDateUtc = useMemo(() => {
    const selectedDay = moment.utc(selectedDate).format('YYYY-MM-DD');
    return {
      from: moment.tz(selectedDay, anomalyTz).startOf('day').valueOf(),
      to: moment.tz(selectedDay, anomalyTz).endOf('day').valueOf()
    };
  }, [selectedDate, anomalyTz]);

  const deviceTimeZone = timeZone?.name || 'UTC';

  // Same from/to/timeZone range convention eventTimelineSection.tsx/lowPowerSection.tsx already
  // use for this page's other charts, rather than DataUsage24Hours' own generic, date-less
  // "Data Usage Per Hour (Wi-Fi + Mobile)" title.
  const title = t('DATA_USAGE_FOR_RANGE', {
    ns: 'anomalyDetectionBeta',
    from: moment.utc(selectedDateUtc.from).tz(deviceTimeZone).format('Do MMMM HH:mm'),
    to: moment.utc(selectedDateUtc.to).tz(deviceTimeZone).format('Do MMMM HH:mm'),
    timeZone: timeZone?.name || 'UTC'
  });

  // Same axis-title convention every other examination chart on this page already uses.
  const xAxisTitle = italicAxisTitle(t('DEVICE_LOCAL_TIME', { ns: 'translation', timeZone: timeZone?.name ? ` (${timeZone.name})` : '' }));

  const eventNames = useMemo(() => Object.values(getDeviceEvents(platformType, featureToggles)).map(event => event.name), [platformType, featureToggles]);

  const dataFetcher = useCallback(() => {
    return (options: RequestInitWithRetry) => Promise.all([
      getDeviceAnomalyDataUsageHourly({ deviceId, from: selectedDateUtc.from, to: selectedDateUtc.to })(options),
      getSelectedTzDateDeviceEventsList({ deviceId, to: selectedDateUtc.to, tz: anomalyTz, events: eventNames })(options)
    ]) as Promise<SectionData>;
  }, [deviceId, selectedDateUtc.from, selectedDateUtc.to, anomalyTz, eventNames]);

  const {
    loading,
    data: [rawDataUsage, rawEvents]
  } = useWorldRequest(dataFetcher, { initialLoading: true, initialData: [[], []] as SectionData });


  const [hasBecomeVisible, setHasBecomeVisible] = useState(false);

  useEffect(() => {
    if (!loading) {
      setHasBecomeVisible(true);
    }
  }, [loading]);

  const chartRef = useRef<am4charts.XYChart | null>(null);
  const handleChartMount = useCallback((chart: am4charts.XYChart) => {
    chartRef.current = chart;
    return chart;
  }, []);
  useEffect(() => {
    if (hasBecomeVisible && chartRef.current) {
      onChartMount(chartRef.current);
    }
  }, [hasBecomeVisible, onChartMount]);

  const activeEvents = useMemo(() => getActiveEventFilters(eventFilters.appliedEventFilters), [eventFilters.appliedEventFilters]);
  const [dataUsageEvents] = useMemo(() => filterDeviceEvents(rawEvents, activeEvents), [rawEvents, activeEvents]);

  return (
    <div className="selectedDateCharts_container device-anomaly-examination-page__data-usage-chart">
      <Loading isLoading={loading} transparentOverlay={false} hideChildren={true}>
        <DataUsage24Hours
          classname="selected-date"
          data={rawDataUsage}
          dateRange={selectedDateUtc}
          platformType={platformType}
          displayTimeZone={deviceTimeZone}
          events={dataUsageEvents}
          eventFilters={eventFilters}
          dispatchEventFilters={dispatchEventFilters}
          visible={hasBecomeVisible}
          onMount={handleChartMount}
          title={title}
          xAxisTitle={xAxisTitle}
          showApplyAll={false}
        />
      </Loading>
    </div>
  );
}
